import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useBrand } from '../contexts/BrandContext';
import { Headphones, Building2, Mail, Lock, Phone, Globe, MapPin, Loader2, CheckCircle, Clock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
    { value: 'tour_operator', label: 'Tour Operator' },
    { value: 'dmc', label: 'DMC (Destination Management)' },
    { value: 'ota', label: 'OTA (Online Travel Agency)' },
    { value: 'hotel', label: 'Hotel / Resort' },
    { value: 'museum', label: 'Museum / Gallery' },
    { value: 'concierge', label: 'Concierge Service' },
    { value: 'other', label: 'Other' },
];

export default function OperatorRegistration() {
    const brand = useBrand();
    const navigate = useNavigate();
    const [regEnabled, setRegEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [form, setForm] = useState({
        company_name: '',
        email: '',
        password: '',
        phone: '',
        business_type: 'tour_operator',
        country: '',
        website: '',
    });

    useEffect(() => {
        fetch(`${API}/api/public/registration-config`)
            .then(r => r.json())
            .then(d => setRegEnabled(d.registration_enabled !== false))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.company_name.trim() || !form.email.trim() || !form.password) return;
        if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }

        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/auth/register-operator`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) { toast.error(data.detail || 'Registration failed'); return; }

            setResult(data);
            if (data.status === 'approved') {
                toast.success('Registration successful!');
                setTimeout(() => navigate('/operator/dashboard'), 2000);
            }
        } catch (e) {
            toast.error('Registration failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-gold animate-spin" />
            </div>
        );
    }

    if (result) {
        const isApproved = result.status === 'approved';
        return (
            <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center space-y-6">
                    <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center ${isApproved ? 'bg-emerald-500/10' : 'bg-gold/10'}`}>
                        {isApproved ? <CheckCircle className="w-10 h-10 text-emerald-400" /> : <Clock className="w-10 h-10 text-gold" />}
                    </div>
                    <h1 className="font-outfit text-2xl font-bold text-white">{isApproved ? 'Welcome Aboard!' : 'Registration Submitted'}</h1>
                    <p className="text-muted-foreground">{result.message}</p>
                    {isApproved ? (
                        <p className="text-sm text-gold">Redirecting to your dashboard...</p>
                    ) : (
                        <Link to="/login">
                            <Button className="bg-gold text-[#0A0D14] hover:bg-gold/90 mt-4">Go to Login</Button>
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0D14] flex" data-testid="operator-registration">
            {/* Left side - branding */}
            <div className="hidden lg:flex lg:w-5/12 bg-gradient-to-br from-gold/10 to-transparent flex-col justify-between p-10">
                <div>
                    <Link to="/" className="flex items-center gap-3 mb-16">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-9 max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-10 h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-5 h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-xl text-white">{brand.platform_name}</span>}
                    </Link>
                    <h2 className="font-outfit text-3xl font-bold text-white mb-4">Partner with {brand.platform_name}</h2>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                        Offer immersive audio experiences to your clients. Generate redeem codes, customize branding, and grow your travel business.
                    </p>
                </div>
                <div className="space-y-4 text-sm text-white/50">
                    <div className="flex items-center gap-3"><CheckCircle className="w-4 h-4 text-gold shrink-0" /> White-label audio guides for your brand</div>
                    <div className="flex items-center gap-3"><CheckCircle className="w-4 h-4 text-gold shrink-0" /> Multi-language support (10+ languages)</div>
                    <div className="flex items-center gap-3"><CheckCircle className="w-4 h-4 text-gold shrink-0" /> Offline & online listening for travelers</div>
                    <div className="flex items-center gap-3"><CheckCircle className="w-4 h-4 text-gold shrink-0" /> Credit-based code generation system</div>
                </div>
            </div>

            {/* Right side - form */}
            <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="w-full max-w-lg">
                    <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-8 transition-colors lg:hidden">
                        <ArrowLeft className="w-4 h-4" /> Back to home
                    </Link>
                    <Link to="/" className="hidden lg:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-8 transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>

                    <h1 className="font-outfit text-2xl font-bold text-white mb-2">Create your partner account</h1>
                    <p className="text-muted-foreground text-sm mb-8">Start offering audio experiences to your travelers</p>

                    {!regEnabled ? (
                        <div className="text-center py-12 bg-[#121620] rounded-xl border border-white/10">
                            <Clock className="w-12 h-12 text-gold/40 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-white mb-2">Registration is currently closed</h3>
                            <p className="text-muted-foreground text-sm">Please contact us to become a partner.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5" data-testid="registration-form">
                            <div className="space-y-2">
                                <Label className="text-white/70">Company Name *</Label>
                                <div className="relative">
                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Your company name" className="pl-10 bg-[#121620] border-white/10 h-11" required data-testid="reg-company-name" />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Email *</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="work@company.com" className="pl-10 bg-[#121620] border-white/10 h-11" required data-testid="reg-email" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Password *</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min 8 characters" className="pl-10 bg-[#121620] border-white/10 h-11" required data-testid="reg-password" />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Phone</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 234 567 890" className="pl-10 bg-[#121620] border-white/10 h-11" data-testid="reg-phone" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Business Type</Label>
                                    <select value={form.business_type} onChange={e => set('business_type', e.target.value)} className="w-full h-11 rounded-md bg-[#121620] border border-white/10 text-white px-3 text-sm" data-testid="reg-business-type">
                                        {BUSINESS_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-white/70">Country</Label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input value={form.country} onChange={e => set('country', e.target.value)} placeholder="Italy" className="pl-10 bg-[#121620] border-white/10 h-11" data-testid="reg-country" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-white/70">Website</Label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://company.com" className="pl-10 bg-[#121620] border-white/10 h-11" data-testid="reg-website" />
                                    </div>
                                </div>
                            </div>

                            <Button type="submit" disabled={submitting} className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90 h-12 text-base font-semibold" data-testid="reg-submit-btn">
                                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                                Create Partner Account
                            </Button>

                            <p className="text-xs text-center text-muted-foreground mt-4">
                                Already have an account?{' '}
                                <Link to="/login" className="text-gold hover:underline">Sign in</Link>
                            </p>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
